import * as SalesModel from "./salesModel"
import * as MaterialsModel from "./materialsModel"
import * as SubcontractorModel from "./subcontractorModel"
import * as OverheadModel from "./overheadModel"
import * as LaborModel from './laborModel'
import * as EquipmentModel from './equipmentModel'
import * as BudgetModel from './budgetModel'
import { uniqueId } from '../../helpers/uniqueId'
import { enforceInvariants } from './enforcer'

export type Worksheet = {
  title: string
  budgetWorksheetId: string
  data: State
  isApplied: boolean
  appliedAt: string | null
  metadata?: {
    createdVia: 'budget-builder' | 'manual' | 'import'
    budgetBuilderVersion?: 'legacy' | 'experimental'
  }
}

export type State = {
  salesGoals: SalesModel.SalesGoal[]
  materials: MaterialsModel.Material[]
  subcontractors: SubcontractorModel.Subcontractor[]
  overheadGroups: OverheadModel.OverheadGroup[]
  employees: LaborModel.Employees
  equipmentList: EquipmentModel.EquipmentList
  profit: BudgetModel.ProfitData
}

export type Action =
  | { type: 'WORKSHEET_LOADED', payload: Worksheet }
  | { type: 'WORKSHEET_CREATED', payload: Worksheet }
  | { type: 'ADD_SALES_GOAL', index: number }
  | { type: 'REMOVE_SALES_GOAL', index: number }
  | { type: 'SALES_GOAL_DESCRIPTION_CHANGED', index: number, payload: string }
  | { type: 'SALES_GOAL_ANNUAL_REVENUE_CHANGED', index: number, payload: number }
  | { type: 'ADD_MATERIAL', index: number }
  | { type: 'REMOVE_MATERIAL', index: number }
  | { type: 'MATERIAL_DESCRIPTION_CHANGED', index: number, payload: string }
  | { type: 'MATERIAL_ANNUAL_COST_CHANGED', index: number, payload: number }
  | { type: 'ADD_SUBCONTRACTOR', index: number }
  | { type: 'REMOVE_SUBCONTRACTOR', index: number }
  | { type: 'SUBCONTRACTOR_DESCRIPTION_CHANGED', index: number, payload: string }
  | { type: 'SUBCONTRACTOR_ANNUAL_COST_CHANGED', index: number, payload: number }
  | { type: 'ADD_OVERHEAD', groupName: string, index: number }
  | { type: 'REMOVE_OVERHEAD', groupName: string, index: number }
  | { type: 'OVERHEAD_DESCRIPTION_CHANGED', groupName: string, index: number, payload: string }
  | { type: 'OVERHEAD_ANNUAL_COST_CHANGED', groupName: string, index: number, payload: number }
  | { type: 'ADD_EMPLOYEE', category: LaborModel.Category, index: number }
  | { type: 'REMOVE_EMPLOYEE', category: LaborModel.Category, index: number }
  | { type: 'EMPLOYEE_NAME_CHANGED', category: LaborModel.Category, index: number, payload: string }
  | { type: 'EMPLOYEE_ROLE_CHANGED', category: LaborModel.Category, index: number, payload: string }
  | { type: 'EMPLOYEE_HOURS_WORKED_PER_YEAR_CHANGED', category: LaborModel.Category, index: number, payload: number }
  | { type: 'EMPLOYEE_UNBILLABLE_HOURS_PER_YEAR_CHANGED', category: LaborModel.Category, index: number, payload: number }
  | { type: 'EMPLOYEE_OVERTIME_HOURS_CHANGED', category: LaborModel.Category, index: number, payload: number }
  | { type: 'EMPLOYEE_BONUSES_CHANGED', category: LaborModel.Category, index: number, payload: number }
  | { type: 'HOURLY_EMPLOYEE_HOURLY_WAGE_CHANGED', index: number, payload: number }
  | { type: 'HOURLY_EMPLOYEE_OVERTIME_MULTIPLIER_CHANGED', index: number, payload: number }
  | { type: 'SALARY_EMPLOYEE_ANNUAL_SALARY_CHANGED', index: number, payload: number }
  | { type: 'ADD_EQUIPMENT', category: EquipmentModel.EquipmentType, index: number }
  | { type: 'REMOVE_EQUIPMENT', category: EquipmentModel.EquipmentType, index: number }
  | { type: 'EQUIPMENT_NAME_CHANGED', id: string, payload: string }
  | { type: 'EQUIPMENT_QUANTITY_CHANGED', id: string, payload: number }
  | { type: 'EQUIPMENT_RECOVERY_STRATEGY_CHANGED', id: string, payload: EquipmentModel.RecoveryStrategy }
  | { type: 'EQUIPMENT_BILLABLE_HOURS_PER_YEAR_CHANGED', id: string, payload: number }
  | { type: 'EQUIPMENT_PURCHASE_PRICE_CHANGED', id: string, payload: number }
  | { type: 'EQUIPMENT_YEARS_USED_CHANGED', id: string, payload: number }
  | { type: 'EQUIPMENT_SELL_PRICE_CHANGED', id: string, payload: number }
  | { type: 'EQUIPMENT_MONTHLY_PAYMENT_CHANGED', id: string, payload: number }
  | { type: 'EQUIPMENT_ANNUAL_COST_CHANGED', id: string, payload: number }
  | { type: 'MATERIAL_MARKUP_CHANGED', payload: number }
  | { type: 'EQUIPMENT_MARKUP_CHANGED', payload: number }
  | { type: 'SUBCONTRACTOR_MARKUP_CHANGED', payload: number }
  | { type: 'SEASON_START_DATE_CHANGED', payload: string }
  | { type: 'SEASON_END_DATE_CHANGED', payload: string }
  | { type: 'SEASON_WORKING_DAYS_CHANGED', payload: number }
  | { type: 'DESIRED_NET_PROFIT_MARGIN_CHANGED', payload: number }
  | { type: 'DESIRED_SALES_GOAL_CHANGED', payload: number }
  | { type: 'TITLE_CHANGED', payload: string }

export type Dispatch = (action: Action) => void

export const reducer = (worksheet: Worksheet, action: Action): Worksheet => {
  switch (action.type) {
    case 'WORKSHEET_LOADED':
    case 'WORKSHEET_CREATED':
      return enforceInvariants(action.payload)
    case 'SALES_GOAL_ANNUAL_REVENUE_CHANGED':
      return { ...worksheet, data: {...worksheet.data, salesGoals: SalesModel.updateAnnualRevenue(worksheet.data.salesGoals, action.index, action.payload) } }
    case 'SALES_GOAL_DESCRIPTION_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, salesGoals: SalesModel.updateDescription(worksheet.data.salesGoals, action.index, action.payload) } }
    case 'ADD_SALES_GOAL':
      return { ...worksheet, data: { ...worksheet.data, salesGoals: SalesModel.add(worksheet.data.salesGoals, action.index) } }
    case 'REMOVE_SALES_GOAL':
      return { ...worksheet, data: { ...worksheet.data, salesGoals: SalesModel.remove(worksheet.data.salesGoals, action.index) } }
    case 'MATERIAL_ANNUAL_COST_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, materials: MaterialsModel.updateAnnualCost(worksheet.data.materials, action.index, action.payload) } }
    case 'MATERIAL_DESCRIPTION_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, materials: MaterialsModel.updateDescription(worksheet.data.materials, action.index, action.payload) } }
    case 'ADD_MATERIAL':
      return { ...worksheet, data: { ...worksheet.data, materials: MaterialsModel.add(worksheet.data.materials, action.index) } }
    case 'REMOVE_MATERIAL':
      return { ...worksheet, data: { ...worksheet.data, materials: MaterialsModel.remove(worksheet.data.materials, action.index) } }
    case 'SUBCONTRACTOR_ANNUAL_COST_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, subcontractors: SubcontractorModel.updateAnnualCost(worksheet.data.subcontractors, action.index, action.payload) } }
    case 'SUBCONTRACTOR_DESCRIPTION_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, subcontractors: SubcontractorModel.updateDescription(worksheet.data.subcontractors, action.index, action.payload) } }
    case 'ADD_SUBCONTRACTOR':
      return { ...worksheet, data: { ...worksheet.data, subcontractors: SubcontractorModel.add(worksheet.data.subcontractors, action.index) } }
    case 'REMOVE_SUBCONTRACTOR':
      return { ...worksheet, data: { ...worksheet.data, subcontractors: SubcontractorModel.remove(worksheet.data.subcontractors, action.index) } }
    case 'REMOVE_OVERHEAD':
      return { ...worksheet, data: { ...worksheet.data, overheadGroups: OverheadModel.remove(worksheet.data.overheadGroups, action.groupName, action.index) } }
    case 'ADD_OVERHEAD':
      return { ...worksheet, data: { ...worksheet.data, overheadGroups: OverheadModel.add(worksheet.data.overheadGroups, action.groupName, action.index) } }
    case 'OVERHEAD_DESCRIPTION_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, overheadGroups: OverheadModel.updateDescription(worksheet.data.overheadGroups, action.groupName, action.index, action.payload) } }
    case 'OVERHEAD_ANNUAL_COST_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, overheadGroups: OverheadModel.updateAnnualCost(worksheet.data.overheadGroups, action.groupName, action.index, action.payload) } }
    case 'ADD_EMPLOYEE':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.addEmployee(worksheet.data.employees, action.category, action.index)} }
    case 'REMOVE_EMPLOYEE':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.removeEmployee(worksheet.data.employees, action.category, action.index)} }
    case 'EMPLOYEE_NAME_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateName(worksheet.data.employees, action.category, action.index, action.payload)} }
    case 'EMPLOYEE_ROLE_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateRole(worksheet.data.employees, action.category, action.index, action.payload)} }
    case 'EMPLOYEE_HOURS_WORKED_PER_YEAR_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateHoursWorkedPerYear(worksheet.data.employees, action.category, action.index, action.payload)} }
    case 'EMPLOYEE_UNBILLABLE_HOURS_PER_YEAR_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateUnbillableHoursPerYear(worksheet.data.employees, action.category, action.index, action.payload)} }
    case 'EMPLOYEE_OVERTIME_HOURS_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateOvertimeHours(worksheet.data.employees, action.category, action.index, action.payload)} }
    case 'EMPLOYEE_BONUSES_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateBonuses(worksheet.data.employees, action.category, action.index, action.payload)} }
    case 'HOURLY_EMPLOYEE_HOURLY_WAGE_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateHourlyWage(worksheet.data.employees, action.index, action.payload)} }
    case 'HOURLY_EMPLOYEE_OVERTIME_MULTIPLIER_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateOvertimeMultiplier(worksheet.data.employees, action.index, action.payload)} }
    case 'SALARY_EMPLOYEE_ANNUAL_SALARY_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, employees: LaborModel.updateAnnualSalary(worksheet.data.employees, action.index, action.payload)} }
    case 'ADD_EQUIPMENT':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.addEquipment(worksheet.data.equipmentList, action.category, action.index )} }
    case 'REMOVE_EQUIPMENT':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.removeEquipment(worksheet.data.equipmentList, action.category, action.index )} }
    case 'EQUIPMENT_NAME_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateName(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_QUANTITY_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateQuantity(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_RECOVERY_STRATEGY_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateRecoveryStrategy(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_BILLABLE_HOURS_PER_YEAR_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateBillableHoursPerYear(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_PURCHASE_PRICE_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updatePurchasePrice(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_YEARS_USED_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateYearsUsed(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_SELL_PRICE_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateSellPrice(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_MONTHLY_PAYMENT_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateMonthlyPayment(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'EQUIPMENT_ANNUAL_COST_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, equipmentList: EquipmentModel.updateAnnualCost(worksheet.data.equipmentList, action.id, action.payload )} }
    case 'MATERIAL_MARKUP_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateMaterialMarkup(worksheet.data.profit, action.payload)} }
    case 'EQUIPMENT_MARKUP_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateEquipmentMarkup(worksheet.data.profit, action.payload)} }
    case 'SUBCONTRACTOR_MARKUP_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateSubcontractorMarkup(worksheet.data.profit, action.payload)} }
    case 'SEASON_START_DATE_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateSeasonStartDate(worksheet.data.profit, action.payload)} }
    case 'SEASON_END_DATE_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateSeasonEndDate(worksheet.data.profit, action.payload)} }
    case 'SEASON_WORKING_DAYS_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateWorkDaysPerWeek(worksheet.data.profit, action.payload)} }
    case 'DESIRED_NET_PROFIT_MARGIN_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateDesiredNetProfitMargin(worksheet.data.profit, action.payload)} }
    case 'DESIRED_SALES_GOAL_CHANGED':
      return { ...worksheet, data: { ...worksheet.data, profit: BudgetModel.updateDesiredSalesGoal(worksheet.data.profit, action.payload)} }
    case 'TITLE_CHANGED':
      return { ...worksheet, title: action.payload }
    default: return worksheet
  }
}

export const createDefaultWorksheet = (): Worksheet => ({
  title: 'New Budget Worksheet',
  budgetWorksheetId: uniqueId(),
  data: createDefaultState(),
  isApplied: false,
  appliedAt: null,
  metadata: {
    createdVia: 'manual'
  }
})

export const createDefaultState = (): State => ({
  salesGoals: SalesModel.createDefaultSalesGoals(),
  materials: MaterialsModel.createDefaultMaterials(),
  subcontractors: SubcontractorModel.createDefaultSubcontractors(),
  overheadGroups: OverheadModel.createDefaultOverhead(),
  employees: LaborModel.createDefaultEmployees(),
  equipmentList: EquipmentModel.createDefaultEquipment(),
  profit: BudgetModel.createDefaultProfitData()
})

export const formatDollarsAndCents = (money: number) => {
  return money.toLocaleString('en-US', {style: 'currency', currency: 'USD'})
}

export const formatDollars = (money: number) => {
  return money.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0})
}

export const formatPercentage = (value: number) => {
  return value.toLocaleString('en-US', { style: 'percent', maximumFractionDigits: 2 })
}

export type BudgetSummary = {
  sales: number
  labor: number
  materials: number
  equipment: number
  subcontractors: number
  overhead: number
  net: number
  equipmentListWithStats: EquipmentModel.EquipmentListWithStats
  equipmentStatistics: EquipmentModel.EquipmentStatistics
  laborStatistics: LaborModel.LaborStatistics
}

export const defaultBudgetSummary = (): BudgetSummary => ({
  sales: 0,
  labor: 0,
  materials: 0,
  equipment: 0,
  subcontractors: 0,
  overhead: 0,
  net: 0,
  equipmentListWithStats: { owned: [], leased: [], misc: [] },
  equipmentStatistics: { overheadAnnualCost: 0, billableAnnualCost: 0, totalAnnualCost: 0 },
  laborStatistics: {
    annualCostOfLabor: 0,
    annualHours: 0,
    billableHours: 0,
    billableCost: 0,
    unbillableHours: 0,
    unbillablePercent: 0,
    unbillableCost: 0,
    manHourCost: 0,
    overtimeFactor: 0
  }
})

export const getSummary = (state: State, metadata?: Worksheet['metadata']): BudgetSummary => {
  // Check if this is a BudgetBuilder worksheet that needs special calculations
  if (metadata?.createdVia === 'budget-builder' && metadata.budgetBuilderVersion) {
    return getBudgetBuilderSummary(state, metadata.budgetBuilderVersion)
  }

  // Standard calculations for manually created/imported worksheets
  const sales = SalesModel.sumSalesGoals(state.salesGoals)
  const laborStatistics = LaborModel.calculateLaborStatistics(state.employees)
  const labor = laborStatistics.billableCost
  const materials = MaterialsModel.sumMaterials(state.materials)
  const equipmentListWithStats = EquipmentModel.calculateItemStatistics(state.equipmentList)
  const equipmentStatistics = EquipmentModel.calculateOverallStatistics(equipmentListWithStats)
  const equipment = equipmentStatistics.totalAnnualCost
  const subcontractors = SubcontractorModel.sumSubcontractors(state.subcontractors)
  const overhead = OverheadModel.sumOverhead(state.overheadGroups)
  const net = sales - (labor + materials + equipment + subcontractors + overhead)
  return {
    sales,
    labor,
    materials,
    equipment,
    subcontractors,
    overhead,
    net,
    equipmentListWithStats,
    equipmentStatistics,
    laborStatistics
  }
}

/**
 * Get budget summary using BudgetBuilder-specific calculations
 */
const getBudgetBuilderSummary = (state: State, version: 'legacy' | 'experimental'): BudgetSummary => {
  // Lazy require: calculations imports types from this module, so a top-level import would be circular
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const BBCalcs = require('./BudgetBuilder/calculations')

  const sales = SalesModel.sumSalesGoals(state.salesGoals)
  const laborStatistics = BBCalcs.calculateLaborStatistics(state.employees, version)
  const labor = laborStatistics.billableCost
  const materials = MaterialsModel.sumMaterials(state.materials)
  const equipmentListWithStats = BBCalcs.calculateItemStatistics(state.equipmentList, version)
  const equipmentStatistics = BBCalcs.calculateOverallStatistics(equipmentListWithStats, version)
  const equipment = equipmentStatistics.totalAnnualCost
  const subcontractors = SubcontractorModel.sumSubcontractors(state.subcontractors)
  const overhead = OverheadModel.sumOverhead(state.overheadGroups)
  const net = sales - (labor + materials + equipment + subcontractors + overhead)
  return {
    sales,
    labor,
    materials,
    equipment,
    subcontractors,
    overhead,
    net,
    equipmentListWithStats,
    equipmentStatistics,
    laborStatistics
  }
}

export type SaveState = "UNSAVED" | "SAVING" | "SAVED" | "ERROR"

export type BudgetReference = {
  label: string
  key: string
}

export type ApplyBudgetData = {
  budgetKey: string
  laborOverhead: number
  equipmentOverhead: number
  materialsOverhead: number
  subcontractorOverhead: number
  profitMargin: number
}
